import 'react-day-picker/style.css';

import Popover from '@mui/material/Popover';
import { styled } from '@mui/system';
import { Box } from '@src/common/components/Box';
import { Button } from '@src/common/components/button/Button';
import { DateInput } from '@src/common/components/dates/DateInput';
import { useDateRangeFormatter } from '@src/common/components/dates/hooks/useDateRangeFormatter';
import { useDateRangeState } from '@src/common/components/dates/hooks/useDateRangeState';
import { usePopover } from '@src/common/components/dates/hooks/usePopover';
import type { TextInput } from '@src/common/components/TextInput';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import type { ComponentProps, ForwardedRef } from 'react';
import { useEffect } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker, TZDate } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

const EMPTY_RANGE: DateRange = { from: undefined, to: undefined };

export const DateRangePicker = forwardRef(function CustomInput(
  {
    onChange,
    onBlur,
    value = EMPTY_RANGE,
    isUTC,
    endAdornment,
    ...props
  }: Omit<ComponentProps<typeof TextInput>, 'onChange' | 'onBlur' | 'value'> & {
    onChange: (newRange: DateRange) => void;
    endAdornment?: JSX.Element;
    value?: DateRange;
    onBlur?: () => void;
    isUTC?: boolean;
  },
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => inputRef.current!, []);

  const formatDateRange = useDateRangeFormatter(isUTC);

  const { tempRange, setTempRange, setIsTyping, range, setRange } =
    useDateRangeState(value, formatDateRange);
  const { anchorElement, openPopover, closePopover, isOpen } = usePopover();

  useEffect(() => {
    const currentPickerValueFrom = range.pickerValue.from?.toISOString();
    const currentPickerValueTo = range.pickerValue.to?.toISOString();
    const newValueFrom = value.from?.toISOString();
    const newValueTo = value.to?.toISOString();

    if (
      newValueFrom !== currentPickerValueFrom ||
      newValueTo !== currentPickerValueTo
    ) {
      setRange({
        inputValue: formatDateRange(value),
        pickerValue: value,
      });
      setTempRange(value);
    }
  }, [value, formatDateRange, range.pickerValue, setRange, setTempRange]);

  const updateRange = (newRange: DateRange) => {
    const validatedRange = validateRange(newRange);
    const newPresentation = {
      inputValue: formatDateRange(validatedRange),
      pickerValue: validatedRange,
    };
    setRange(newPresentation);
    setTempRange(validatedRange);
    onChange(validatedRange);
  };

  const handleInputChange = (newValue: string) => {
    setIsTyping(true);

    const validatedRange = parseUserInput(newValue, isUTC);

    setRange({
      inputValue: newValue,
      pickerValue: validatedRange,
    });
    setTempRange(validatedRange);
    onChange(validatedRange);
  };

  const handleInputBlur = () => {
    setIsTyping(false);
    setRange({
      inputValue: formatDateRange(range.pickerValue),
      pickerValue: range.pickerValue,
    });
    onBlur?.();
  };

  const handleDayPickerSelect = (selectedRange?: DateRange) => {
    const validatedRange = selectedRange
      ? validateRange(selectedRange)
      : EMPTY_RANGE;
    setTempRange(validatedRange);
  };

  const handleOk = () => {
    updateRange(tempRange);
    closePopover();
  };

  const handleCancel = () => {
    setTempRange(range.pickerValue);
    closePopover();
  };

  const handleClear = () => {
    updateRange(EMPTY_RANGE);
  };

  const handleCalendarIconClick = () => {
    setTempRange(range.pickerValue);
    openPopover(inputRef.current);
  };

  const defaultMonth = (() => {
    const referenceDate = tempRange.from || new Date();
    return isUTC ? new TZDate(referenceDate, 'UTC') : referenceDate;
  })();

  const selected = (() => {
    if (isEmptyRange(tempRange.from, tempRange.to)) return undefined;
    if (isUTC) {
      return {
        from: tempRange.from ? new TZDate(tempRange.from, 'UTC') : undefined,
        to: tempRange.to ? new TZDate(tempRange.to, 'UTC') : undefined,
      };
    }

    return tempRange;
  })();

  return (
    <>
      <DateInput
        {...props}
        inputRef={inputRef}
        value={range.inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleInputBlur}
        onCalendarClick={handleCalendarIconClick}
        endAdornment={endAdornment}
        placeholder={t('dateRangePlaceholder')}
      />
      <Popover
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box>
          <StyledDateRangePicker
            timeZone={isUTC ? 'UTC' : undefined}
            defaultMonth={defaultMonth}
            mode="range"
            selected={selected}
            onSelect={handleDayPickerSelect}
            required={false}
          />
          <Box
            display="flex"
            alignItems="center"
            sx={{
              backgroundColor: theme.palette.surface.surfaceContainer,
              padding: theme.spacing['2'],
            }}
          >
            <Button
              variant="text"
              onClick={handleClear}
              size="small"
              sx={{ ml: -1 }}
            >
              {t('clear')}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="text" onClick={handleCancel} size="small">
              {t('cancel')}
            </Button>
            <Button variant="text" onClick={handleOk} size="small">
              OK
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

const StyledDateRangePicker = styled(DayPicker)`
  padding: ${theme.spacing['2']};
  font-family: 'IBM Plex Sans', sans-serif;
  background-color: ${theme.palette.surface.surfaceContainer};
  --rdp-range_middle-background-color: ${theme.palette.primary
    .primaryContainer} !important;
  --rdp-accent-color: ${theme.palette.primary.primary} !important;
  --rdp-today-color: ${theme.palette.primary.inversePrimary} !important;
`;

const isReverseRange = (from?: Date, to?: Date): boolean =>
  !!(from && to && from > to);

const validateRange = (range: DateRange): DateRange => {
  const { from, to } = range;
  return isReverseRange(from, to) ? { from: to, to: from } : range;
};

const parseDate = (dateString: string, isUTC?: boolean): Date | undefined => {
  const trimmed = dateString.trim();
  return isUTC
    ? Timestamp.tryUTCFromDDMMYYYY(trimmed)?.getDate()
    : Timestamp.tryFromDDMMYYYY(trimmed)?.getDate();
};

const parseUserInput = (input: string, isUTC?: boolean): DateRange => {
  if (!input.trim()) {
    return EMPTY_RANGE;
  }
  const rangeMatch = matchDateRange(input);
  if (rangeMatch?.groups) {
    return parseRange(rangeMatch.groups, isUTC);
  }
  return createRangeFromParts(input, isUTC);
};

const matchDateRange = (newValue: string) => {
  const dateMatcher = /[0-9]{1,2}[-/]?[0-9]{1,2}[-/]?[0-9]{4}/;
  const dateRangeMatcher = new RegExp(
    `(?<from>${dateMatcher.source})(?:\\s*-\\s*)?(?<to>${dateMatcher.source})`,
  );

  return newValue.match(dateRangeMatcher);
};
const parseRange = (
  groups: { [p: string]: string },
  isUTC: undefined | boolean,
) => {
  const fromDateStr = groups.from;
  const toDateStr = groups.to;
  const fromDate = parseDate(fromDateStr, isUTC);
  const toDate = parseDate(toDateStr, isUTC);

  return validateRange({ from: fromDate, to: toDate });
};
const createRangeFromParts = (newValue: string, isUTC?: boolean): DateRange => {
  const parts = newValue.split(' - ');
  if (parts.length === 2) {
    const fromDate = parseDate(parts[0], isUTC);
    const toDate = parseDate(parts[1], isUTC);
    return validateRange({ from: fromDate, to: toDate });
  }

  if (parts.length === 1 && parts[0].trim()) {
    const singleDate = parseDate(parts[0], isUTC);
    return { from: singleDate, to: undefined };
  }

  return EMPTY_RANGE;
};
const isEmptyRange = (from?: Date, to?: Date): boolean => !from && !to;
